import Color from 'color'
import { ButtonVariant } from '../components/shared/buttons/types'

/** Indigo blue fallback used when an institution's primaryColor is not configured. */
export const DEFAULT_PRIMARY_COLOR = '#3F51B5'

function getRGB(c: string): number {
  let rgb
  try {
    rgb = parseInt(c, 16)
  } catch (err) {
    rgb = 0
  }
  return rgb
}

function getsRGB(c: string): number {
  const color = getRGB(c) / 255
  return color <= 0.03928 ? color / 12.92 : ((color + 0.055) / 1.055) ** 2.4
}

function calcHsp(color: string): number {
  const r = getRGB(color.substr(1, 2))
  const g = getRGB(color.substr(3, 2))
  const b = getRGB(color.substr(5, 2))

  // HSP equation from http://alienryderflex.com/hsp.html
  return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b))
}

function isDark(color: string): boolean {
  const hsp = calcHsp(color)

  // Using the HSP value, a value < 128 is dark
  return hsp < 129
}

function isVeryDark(color: string): boolean {
  const hsp = calcHsp(color)

  // Using the HSP value, a value < 40 is very dark
  return hsp < 41
}

function getLuminosity(c: string): number {
  return (
    0.2126 * getsRGB(c.substr(1, 2)) +
    0.7152 * getsRGB(c.substr(3, 2)) +
    0.0722 * getsRGB(c.substr(-2))
  )
}

// Works for both three and six character hex values
export function isHex(c: string): boolean {
  return /^#([0-9A-F]{3}){1,2}$/i.test(c)
}

// Contrast equation from https://webaim.org/resources/contrastchecker
function checkContrast(background: string, font: string): number {
  const fontLum = getLuminosity(font)
  const backgroundLum = getLuminosity(background)
  return (Math.max(fontLum, backgroundLum) + 0.05) / (Math.min(fontLum, backgroundLum) + 0.05)
}

// Determine if the font color should be black (#000)
// or white (#fff) based on contrast
// with the background color
export function getFontColor(backgroundColor: string): string {
  if (!backgroundColor || !isHex(backgroundColor) || backgroundColor === '#000') {
    return '#ffffff'
  }

  if (backgroundColor === '#fff') {
    return '#000000'
  }

  const dark = isDark(backgroundColor)

  // Sometimes we get a false positive for isDark, double check the contrast with white text
  if (dark) {
    const ratio = checkContrast(backgroundColor, '#ffffff')
    // if the contrast is less than 4.5, use black instead
    return ratio < 4.5 ? '#000000' : '#ffffff'
  }
  return '#000000'
}

export function getFocusColor(
  backgroundColor: string,
  variant?: ButtonVariant,
  isNav?: boolean
): string {
  let focusColor: string = ''
  if (variant) {
    if (variant === ButtonVariant.Icon) {
      focusColor = Color(backgroundColor).alpha(0.09).toString()
    } else if (variant === ButtonVariant.Secondary || variant === ButtonVariant.Outline) {
      focusColor = Color(backgroundColor).alpha(0.04).toString()
    }
  }

  // No variant passed in, or the variant is the primary, this is likely a button.  Darken or lighten accordingly
  if (!focusColor) {
    const isColorDark = isDark(backgroundColor) && checkContrast(backgroundColor, '#ffffff') >= 4.5

    // Super dark colors don't work in the below calc, defaulting to hardcoded value
    if (isNav && isVeryDark(backgroundColor)) {
      focusColor = '#FFFFFF40' // white at 25% opacity
    } else if (isColorDark) {
      focusColor = Color(backgroundColor).alpha(0.75).lighten(0.25).toString()
    } else {
      focusColor = Color(backgroundColor).darken(0.25).toString()
    }
  }
  return focusColor
}
